import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/lib/hooks/useMediaQuery';
import BookmarkIcon from '@/icons/Bookmark.svg?react';

/**
 * Content interface for OpenCatalogButton component
 */
export interface OpenCatalogButtonContent {
  /** Button label text (item count is appended automatically) */
  label: string;
}

/**
 * Props interface for OpenCatalogButton component
 */
export interface OpenCatalogButtonProps {
  /** Content containing button label */
  content: OpenCatalogButtonContent;
  /** Number of items in the catalog */
  itemCount: number;
  /** Whether the catalog is currently open (hides button when true) */
  isCatalogOpen?: boolean;
  /** Click handler to open the catalog */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * OpenCatalogButton - Fixed position button to open the Resource Catalog
 *
 * Displays a primary large button with a bookmark icon in the top-right
 * corner of the page. The button follows the user as they scroll.
 * Clicking the button opens the Resource Catalog slide-out panel.
 *
 * Visual specs:
 * - Primary large variant button with icon
 * - Fixed position, top-right corner
 * - Border radius removed on top-right and top-left (rounded-br-[6px] rounded-bl-[6px])
 * - Text: "Saved Resources (X)" where X is the item count
 *
 * @example
 * ```tsx
 * import { OpenCatalogButton } from '@/components';
 *
 * <OpenCatalogButton
 *   content={{ label: 'Saved Resources' }}
 *   itemCount={3}
 *   onClick={() => setIsCatalogOpen(true)}
 * />
 * ```
 */
export const OpenCatalogButton = forwardRef<HTMLButtonElement, OpenCatalogButtonProps>(
  ({ content, itemCount, isCatalogOpen = false, onClick, className }, ref) => {
    const isMobile = useIsMobile();

    // Hide button when catalog is open
    if (isCatalogOpen) {
      return null;
    }

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={cn(
          // Base styles - primary large button with icon
          'inline-flex items-center justify-center gap-2',
          'h-14 px-4',
          'bg-button-primary text-white',
          'font-semibold text-xl leading-7',
          // Modified border radius: 6px on left side, squared on right side
          'rounded-tl-[6px] rounded-bl-[6px] rounded-tr-none rounded-br-none',
          // Fixed position in top-right (140px header + 24px gap = 164px)
          // Use max-w-fit to prevent horizontal overflow
          'fixed z-[130]',
          'right-0 top-[164px]',
          // Hover and focus states (no ring-offset to prevent overflow at edge)
          'hover:bg-button-primary-hover',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4D8F] focus-visible:ring-offset-2',
          // Transition
          'transition-colors duration-150',
          'motion-reduce:transition-none',
          'cursor-pointer',
          className
        )}
        aria-label={`${content.label} (${itemCount} items)`}
      >
        <BookmarkIcon className="h-6 w-6 [&_path]:fill-white" aria-hidden="true" />
        <span>
          {isMobile ? `(${itemCount})` : `${content.label} (${itemCount})`}
        </span>
      </button>
    );
  }
);

OpenCatalogButton.displayName = 'OpenCatalogButton';

export default OpenCatalogButton;
